import { collectCustomFields, getParamOrHidden } from './utils.js';
import { setLoading, showFeedback, showErrorBox, showToast } from './form-helpers.js';
import {
  createCheckoutForTicket,
  registerForEvent,
} from './tickets.api.js';
import {
  getBotProtectionConfig,
  injectBotProtection,
  resolveBotProtectionResponse,
  refreshBotProtection,
} from './bot-protection.js';
import { extractErrorMessage } from './error-utils.js';
import { revealWaitlistFormForTicket, showWaitlistConfirmation } from './waitlist-management.js';
import { escapeHtml } from './sanitize.js';
import { safeRedirect } from '../checkout-core/security.js';
import { initLiveValidation, generateCustomFieldRules, validateCustomFields } from '../checkout-core/validation.js';
import { API_BASE, apiCall } from '../checkout-core/api.js';
import { collectCheckoutPayload, validateCheckoutState, resetCheckoutState } from './checkout-enhancements.js';

const __ = (typeof window.__ === 'function')
  ? window.__
  : function(message, replacements) {
    const text = String(message == null ? '' : message);
    if (!Array.isArray(replacements) || replacements.length === 0) return text;
    return text.replace(/\{(\d+)\}/g, (match, idx) => {
      const value = replacements[Number(idx)];
      return value === undefined || value === null ? match : String(value);
    });
  };

function getMissingProfileFields(userData, ageVerificationMode = 'birth_date') {
  const missing = [];
  if (!userData?.first_name) {
    missing.push(__('Vorname'));
  }
  if (!userData?.last_name) {
    missing.push(__('Nachname'));
  }
  if (ageVerificationMode === 'birth_date' && !userData?.birth_date) {
    missing.push(__('Geburtsdatum'));
  }
  return missing;
}

function ensureCashNotice(message) {
  const msg = (message || '').trim();
  if (!msg) {
    return __('Registrierung erfolgreich! Bitte bezahle vor Ort.');
  }
  const lower = msg.toLowerCase();
  if (lower.includes('bar') || lower.includes('vor ort') || lower.includes('vor-ort') || lower.includes('cash')) {
    return msg;
  }
  return `${msg} ${__('Bitte bezahle vor Ort.')}`;
}

function validateAge(birthDate, minAge, ageVerificationMode = 'birth_date', ageConfirmed = false) {
  if (!minAge || minAge <= 0) return { valid: true };

  // Checkbox mode: only check age_confirmed
  if (ageVerificationMode === 'checkbox') {
    if (!ageConfirmed) {
      return { valid: false, message: __('Bitte bestätige dein Alter.') };
    }
    return { valid: true };
  }

  // Birth date mode: validate actual birth date
  const birth = birthDate ? new Date(birthDate) : null;
  if (!birth || Number.isNaN(birth.getTime())) {
    return {
      valid: false,
      message: __('Bitte vervollständige deine Angaben unter /core/account-verwalten.'),
    };
  }
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const month = today.getMonth() - birth.getMonth();
  if (month < 0 || (month === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }
  if (age < minAge) {
    return {
      valid: false,
      message: __('Du musst mindestens {0} Jahre alt sein, um dieses Ticket zu buchen.', [minAge]),
    };
  }
  return { valid: true };
}

/**
 * Briefly flash submit button green to signal success before redirect.
 * @param {HTMLButtonElement} btn
 */
function flashSuccess(btn) {
  if (!btn) return;
  btn.classList.add('checkout-btn--success');
  const textEl = btn.querySelector('.checkout-btn-text, .btn-text');
  if (textEl) textEl.textContent = __('Erfolgreich!');
  // CSS handles the green flash animation; class removed after redirect/reload
}

/**
 * WELLE 2 (P2-1): Build the tokenless /checkout/success fallback URL so every
 * successful registration terminal converges on the success page even when the
 * backend could not mint a checkout token (ticket_url missing). The state drives
 * the generic, PII-free banner; safeRedirect (http/redirect.js) permits this
 * relative same-origin path.
 * @param {{requiresApproval?: boolean, isCash?: boolean}} [opts]
 * @returns {string}
 */
function buildCheckoutSuccessFallbackUrl({ requiresApproval = false, isCash = false } = {}) {
  let state = 'confirmed';
  if (requiresApproval) state = 'pending_approval';
  else if (isCash) state = 'pay_on_site';
  return '/checkout/success?state=' + state;
}

export function initTicketRegistration(root, config, { softReload } = {}) {
  if (!root) {
    return;
  }
  const forms = Array.from(root.querySelectorAll('.register-form'));
  if (!forms.length) {
    return;
  }

  forms.forEach((form) => {
    if (form.dataset.ticketSubmitInit === '1') {
      return;
    }
    form.dataset.ticketSubmitInit = '1';

    // Initialize live validation (WCAG 3.3.1/3.3.3 compliance)
    // Support both data-min-age (modern templates) and data-minage (legacy)
    const minAge = parseInt(form.dataset.minAge || form.dataset.minage || '0', 10);
    const ageVerificationMode = form.dataset.ageMode || form.dataset.agemode || form.dataset.ageverificationmode || 'birth_date';
    initLiveValidation(form, { minAge, ageVerificationMode });

    // Bot protection is deferred to form expansion (openInlineCheckout)
    // to avoid N parallel API calls + DOM injections for hidden forms.
    // The submit handler (below) has its own getBotProtectionConfig fallback.

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      // Double-submission guard
      if (form.dataset.submitting === '1') return;
      form.dataset.submitting = '1';
      let keepSubmitGuard = false;

      const userData = config?.accountData;
      const ticket = form.dataset.ticket;
      // Support both data-min-age (modern templates) and data-minage (legacy)
      const minAge = parseInt(form.dataset.minAge || form.dataset.minage || '0', 10);
      const isPaid = String(form.dataset.ispaid || '0') === '1';
      const isFree = String(form.dataset.isfree || '0') === '1';
      const isCash = !isPaid && !isFree;
      const requiresApproval = String(form.dataset.requiresApproval || '0') === '1' || form.dataset.requiresApproval === 'true';
      // Support both data-age-mode (kebab) and data-ageverificationmode
      const ageVerificationMode = form.dataset.ageMode || form.dataset.agemode || form.dataset.ageverificationmode || 'birth_date';
      const ageConfirmedEl = form.querySelector('input[name="age_confirmed"]');
      const ageConfirmed = ageConfirmedEl ? ageConfirmedEl.checked : false;
      const feedbackId = form.dataset.feedbackId || '';
      const feedback =
        (feedbackId ? document.getElementById(feedbackId) : null) ||
        document.getElementById(`feedback-${ticket}`) ||
        form.querySelector('.feedback-box') ||
        form;
      const submitBtn = form.querySelector('button[type=submit]');
      if (submitBtn && !submitBtn.dataset.originalText) {
        submitBtn.dataset.originalText = submitBtn.textContent;
      }
      feedback.className = 'feedback-box hidden mt-2';
      feedback.innerHTML = '';

      if (!userData || !userData.cita_id) {
        feedback.textContent = __('Fehler beim Laden der Profildaten. Bitte neu anmelden.');
        feedback.classList.remove('hidden');
        form.dataset.submitting = '0';
        return;
      }

      setLoading(submitBtn, true);

      const consentDsgvo = form.querySelector('input[name="consent_dsgvo"]')?.checked;
      const consentAgb = form.querySelector('input[name="consent_agb"]')?.checked;
      const consentOrganizerEl = form.querySelector('input[name="consent_organizer_terms"]');
      const consentOrganizer = consentOrganizerEl ? consentOrganizerEl.checked : true;
      const consentMarketing = form.querySelector('input[name="consent_marketing"]')?.checked || false;
      const consentEventUpdates = form.querySelector('input[name="consent_event_updates"]')?.checked || false;

      if (!consentDsgvo || !consentAgb || !consentOrganizer) {
        const consentMsg = consentOrganizerEl
          ? __('Bitte akzeptiere DSGVO, AGB und die Veranstalter-AGBs.')
          : __('Bitte akzeptiere DSGVO, AGB.');
        showFeedback(feedback, consentMsg, 'error');
        showToast(consentMsg, 'error');
        setLoading(submitBtn, false);
        form.dataset.submitting = '0';
        return;
      }

      const missingFields = getMissingProfileFields(userData, ageVerificationMode);
      if (missingFields.length > 0) {
        const missingText = escapeHtml(missingFields.join(', '));
        showErrorBox(
          feedback,
          __('Bitte vervollständige dein Profil ({0}) unter <a href="/core/account-verwalten" class="underline">/core/account-verwalten</a>.', [missingText]),
        );
        showToast(__('Bitte vervollständige dein Profil ({0}) unter /core/account-verwalten.', [missingText]), 'error');
        setLoading(submitBtn, false);
        form.dataset.submitting = '0';
        return;
      }

      const ageCheck = validateAge(userData.birth_date, minAge, ageVerificationMode, ageConfirmed);
      if (!ageCheck.valid) {
        showFeedback(feedback, ageCheck.message, 'error');
        showToast(ageCheck.message, 'error');
        setLoading(submitBtn, false);
        form.dataset.submitting = '0';
        return;
      }

      const checkoutValidation = validateCheckoutState(form);
      if (!checkoutValidation.valid) {
        const msg = checkoutValidation.message || __('Bitte prüfe die Angaben für Gruppen/Begleitpersonen.');
        showFeedback(feedback, msg, 'error');
        showToast(msg, 'error');
        setLoading(submitBtn, false);
        form.dataset.submitting = '0';
        return;
      }

      const { payload: checkoutPayload, plusOnes } = collectCheckoutPayload(form);

      const ticketcreativeToken = getParamOrHidden(form, 'ticketcreative_token');
      const inviteToken = getParamOrHidden(form, ['invite', 'invite_token']);
      const inviteCode = getParamOrHidden(form, 'invite_code');
      const accessCode = getParamOrHidden(form, 'access_code');

      let botConfig = null;
      let botResponse = null;
      try {
        botConfig = await getBotProtectionConfig(config?.eventName, { inviteToken, inviteCode });
        if (botConfig?.error_code) {
          const msg = botConfig.message || __('Bot-Schutz ist fehlerhaft konfiguriert.');
          showFeedback(feedback, msg, 'error');
          showToast(msg, 'error');
          setLoading(submitBtn, false);
          form.dataset.submitting = '0';
          return;
        }
        if (botConfig?.enabled && botConfig.mode !== 'none') {
          await injectBotProtection(form, botConfig);
          botResponse = await resolveBotProtectionResponse(botConfig, form);
          if (!botResponse) {
            showFeedback(feedback, __('Bitte die Bot-Verifizierung abschließen.'), 'error');
            showToast(__('Bitte die Bot-Verifizierung abschließen.'), 'error');
            setLoading(submitBtn, false);
            form.dataset.submitting = '0';
            return;
          }
        }
      } catch (error) {
        showFeedback(feedback, __('Bot-Verifizierung konnte nicht geladen werden.'), 'error');
        showToast(__('Bot-Verifizierung konnte nicht geladen werden.'), 'error');
        setLoading(submitBtn, false);
        form.dataset.submitting = '0';
        return;
      }

      const membershipNumberEl = form.querySelector('input[name="membership_number"]');
      const hasMembershipEl = form.querySelector('input[name="has_membership_number"]');
      const membershipRequired = form.dataset.membershipRequired === '1' || form.dataset.membershipRequired === 'true';
      const membershipNumber = membershipRequired && membershipNumberEl
        ? (membershipNumberEl.value || '').trim()
        : hasMembershipEl && hasMembershipEl.checked && membershipNumberEl
        ? (membershipNumberEl.value || '').trim()
        : '';
      if (membershipRequired && !membershipNumber) {
        const msg = __('Bitte gib deine Zusatzangabe an.');
        showFeedback(feedback, msg, 'error');
        showToast(msg, 'error');
        setLoading(submitBtn, false);
        form.dataset.submitting = '0';
        return;
      }
      // Client-side custom field validation
      const customFieldDefs = form.dataset.customFieldDefs
        ? JSON.parse(form.dataset.customFieldDefs)
        : [];
      if (customFieldDefs.length > 0) {
        const customRules = generateCustomFieldRules(customFieldDefs);
        if (!validateCustomFields(form, customRules)) {
          setLoading(submitBtn, false);
          form.dataset.submitting = '0';
          return;
        }
      }

      const customFieldsPayload = collectCustomFields(form);
      const customFieldsJson = customFieldsPayload ? JSON.stringify(customFieldsPayload) : null;
      const eventFallback = `/event/${config?.eventSlug || ''}${accessCode ? '?code=' + encodeURIComponent(accessCode) : ''}`;

      try {
        if (requiresApproval) {
          const approvalResponse = await apiCall(`${API_BASE}.event.event_user_api.request_ticket_approval`, {
            event: config?.eventName,
            ticket_type: ticket,
            consent_dsgvo: true,
            consent_agb: true,
            consent_organizer_terms: consentOrganizer,
            consent_marketing: consentMarketing,
            consent_event_updates: consentEventUpdates,
            membership_number: membershipNumber,
            invite_token: inviteToken,
            invite_code: inviteCode,
            access_code: accessCode || undefined,
            age_confirmed: ageVerificationMode === 'checkbox' ? ageConfirmed : false,
            bot_challenge_response: botResponse || undefined,
            ...checkoutPayload,
            ...(customFieldsJson ? { custom_fields: customFieldsJson } : {}),
            ...(plusOnes.length > 0 ? { plus_ones: JSON.stringify(plusOnes) } : {}),
          });

          if (approvalResponse?.success) {
            const approvalData = approvalResponse?.data || {};
            const baseMsg =
              approvalResponse?.toast ||
              __('Anfrage gesendet! Du erhältst eine E-Mail, sobald deine Anfrage bearbeitet wurde.');

            // WELLE 2 (P2-1): converge on /checkout/success. The backend always
            // returns a ticket_url for approval-pending; the tokenless fallback
            // URL is defense-in-depth so a token-generation failure no longer
            // strands the user on a lone toast (previous softReload terminal).
            const successUrl =
              approvalData?.ticket_url ||
              buildCheckoutSuccessFallbackUrl({ requiresApproval: true });
            showFeedback(feedback, baseMsg, 'success');
            showToast(baseMsg, 'success');
            flashSuccess(submitBtn);
            resetCheckoutState(form);
            setTimeout(() => {
              safeRedirect(successUrl, eventFallback);
            }, 600);
            return;
          }

          const approvalError = extractErrorMessage(approvalResponse, __('Anfrage konnte nicht gesendet werden.'));
          showFeedback(feedback, approvalError, 'error');
          showToast(approvalError, 'error');
          if (botConfig?.enabled && botConfig.mode === 'simple') {
            await refreshBotProtection(form, config?.eventName, { inviteToken, inviteCode });
          }
          setLoading(submitBtn, false);
          return;
        }

        if (isPaid) {
          const response = await createCheckoutForTicket({
            event: config?.eventName,
            ticket_type: ticket,
            ticketcreative_token: ticketcreativeToken,
            invite_token: inviteToken,
            invite_code: inviteCode,
            access_code: accessCode || undefined,
            membership_number: membershipNumber,
            consent_dsgvo: true,
            consent_agb: true,
            consent_organizer_terms: consentOrganizer,
            consent_marketing: consentMarketing,
            consent_event_updates: consentEventUpdates,
            age_confirmed: ageVerificationMode === 'checkbox' ? ageConfirmed : false,
            bot_challenge_response: botResponse || undefined,
            ...(plusOnes.length > 0 ? { plus_ones: JSON.stringify(plusOnes) } : {}),
            ...checkoutPayload,
            ...(customFieldsJson ? { custom_fields: customFieldsJson } : {}),
          });

          if (response?.success) {
            const data = response?.data || {};

            if (data?.free_checkout) {
              const isIdempotent = !!data?.idempotent;
              const toastType = isIdempotent ? 'info' : 'success';
              const successMsg = response?.toast || __('Erfolgreich registriert!');
              showFeedback(feedback, successMsg, toastType);
              showToast(successMsg, toastType);
              if (!isIdempotent) flashSuccess(submitBtn);
              // WELLE 2 (P2-1): free-via-promo also converges on /checkout/success
              // instead of falling back to the event page.
              const redirectUrl = data?.ticket_url || buildCheckoutSuccessFallbackUrl();
              setTimeout(() => {
                safeRedirect(redirectUrl, eventFallback);
              }, 600);
              return;
            }

            if (data?.checkout_url) {
              // FE-005: Use safeRedirect to prevent Open Redirect (CWE-601)
              if (submitBtn) submitBtn.classList.add('is-redirecting');
              safeRedirect(data.checkout_url, eventFallback);
              return;
            }
          }

          // A-2 / H-13: paid, sold-out ticket with waitlist enabled — the server returns
          // SOLD_OUT_WAITLIST_AVAILABLE (data.redirect="waitlist"). Route to the waitlist
          // join instead of dead-ending on a generic Stripe payment error.
          if (response?.code === 'sold_out_waitlist_available' || response?.data?.redirect === 'waitlist') {
            const waitlistMsg = extractErrorMessage(
              response,
              __('Dieses Ticket ist ausverkauft. Du kannst dich auf die Warteliste setzen.'),
            );
            showFeedback(feedback, waitlistMsg, 'info');
            showToast(waitlistMsg, 'info');
            if (!revealWaitlistFormForTicket(ticket)) {
              // Race-sellout: the ticket was available at page-load, so the server-rendered
              // waitlist join button is not in the DOM. Reload the tickets page so the server
              // re-renders the sold-out state (which exposes the waitlist join button).
              setTimeout(() => { try { window.location.reload(); } catch (_) { /* noop */ } }, 1500);
            }
            return;
          }

          const msg = extractErrorMessage(response, __('Zahlung konnte nicht gestartet werden (Stripe).'));
          showFeedback(feedback, msg, 'error');
          showToast(msg, 'error');
          if (botConfig?.enabled && botConfig.mode === 'simple') {
            await refreshBotProtection(form, config?.eventName, { inviteToken, inviteCode });
          }
          setLoading(submitBtn, false);
          return;
        }

        const registerPayload = {
          event: config?.eventName,
          ticket_type: ticket,
          consent_dsgvo: true,
          consent_agb: true,
          consent_organizer_terms: consentOrganizer,
          consent_marketing: consentMarketing,
          consent_event_updates: consentEventUpdates,
          ticketcreative_token: ticketcreativeToken,
          membership_number: membershipNumber,
          invite_token: inviteToken,
          invite_code: inviteCode,
          access_code: accessCode || undefined,
          age_confirmed: ageVerificationMode === 'checkbox' ? ageConfirmed : false,
          bot_challenge_response: botResponse || undefined,
          ...(plusOnes.length > 0 ? { plus_ones: JSON.stringify(plusOnes) } : {}),
          ...checkoutPayload,
          ...(customFieldsJson ? { custom_fields: customFieldsJson } : {}),
        };

        const response = await registerForEvent(registerPayload);

        if (response?.success) {
          const data = response?.data || {};
          const isWaitlist = data?.status === 'Warteliste';

          if (!isWaitlist) {
            // WELLE 2 (P2-1): every non-waitlist success converges on the single
            // /checkout/success terminal. The backend always returns a ticket_url;
            // the tokenless fallback covers states the API does not tokenise (and
            // any token-generation failure) so nothing strands on a silent reload.
            const isIdempotent = !!data?.idempotent;
            const toastType = isIdempotent ? 'info' : 'success';
            const baseMsg =
              response?.toast ||
              (isCash ? __('Registrierung erfolgreich! Bitte bezahle vor Ort.') : __('Anmeldung erfolgreich!'));
            const successMsg = isCash ? ensureCashNotice(baseMsg) : baseMsg;
            showFeedback(feedback, successMsg, toastType);
            showToast(successMsg, toastType);
            if (!isIdempotent) flashSuccess(submitBtn);
            resetCheckoutState(form);
            const successUrl = data?.ticket_url || buildCheckoutSuccessFallbackUrl({ isCash });
            setTimeout(() => {
              safeRedirect(successUrl, `/event/${config?.eventSlug || ''}`);
            }, 600);
            return;
          }

          // Waitlist (OD-1, Option B): persistente Inline-Bestätigungs-Karte statt
          // Soft-Reload. Zeigt die Position (waitlist_info.priority/total), funktioniert
          // ohne Session-Identität und ohne Kontextwechsel — passt zur Natur der
          // Warteschlange (laufender Zustand, kein einmaliger Kauf).
          const waitMsg =
            response?.toast ||
            __('Du bist auf der Warteliste. Wir informieren dich, sobald ein Platz frei wird.');
          showToast(waitMsg, 'success');
          resetCheckoutState(form);
          showWaitlistConfirmation(form, {
            position: data?.waitlist?.priority,
            total: data?.waitlist?.total,
          });
        } else {
          // Server-side error (503/502/504): registration may have been created despite error response
          const SERVER_ERROR_CODES = ['service_unavailable', 'bad_gateway', 'gateway_timeout', 'fetch_failed'];
          if (SERVER_ERROR_CODES.includes(response?.code)) {
            keepSubmitGuard = true;
            const warnMsg = __('Server antwortet nicht. Deine Registrierung wurde möglicherweise bereits erstellt. Bitte prüfe deine E-Mail.');
            showFeedback(feedback, warnMsg, 'warning');
            showToast(warnMsg, 'warning');
            if (submitBtn) {
              submitBtn.disabled = true;
              submitBtn.textContent = __('Bitte E-Mail prüfen');
            }
            // Allow retry after 30s
            setTimeout(() => {
              form.dataset.submitting = '0';
              if (submitBtn) {
                submitBtn.disabled = false;
                submitBtn.textContent = submitBtn.dataset.originalText || __('Registrieren');
              }
            }, 30000);
          } else {
            const msg = extractErrorMessage(response, __('Registrierung fehlgeschlagen.'));
            showFeedback(feedback, msg, 'error');
            showToast(msg, 'error');
            if (botConfig?.enabled && botConfig.mode === 'simple') {
              await refreshBotProtection(form, config?.eventName, { inviteToken, inviteCode });
            }
          }
        }
      } catch (error) {
        const fallbackMsg = isPaid
          ? __('Zahlung konnte nicht gestartet werden (Stripe).')
          : __('Fehler beim Registrieren. Bitte später erneut versuchen.');
        const msg = extractErrorMessage(error, fallbackMsg);
        showFeedback(feedback, msg, 'error');
        showToast(msg, 'error');
        if (botConfig?.enabled && botConfig.mode === 'simple') {
          await refreshBotProtection(form, config?.eventName, { inviteToken, inviteCode });
        }
      } finally {
        setLoading(submitBtn, false);
        if (!keepSubmitGuard) {
          form.dataset.submitting = '0';
        }
      }
    });
  });
}
